import { type QueryKey } from '@tanstack/react-query';
import { type ReactElement, useCallback, useMemo, useState } from 'react';
import { useAuth } from '@/auth/useAuth';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { buildExecutionRunsColumns } from '@/features/executionRuns/components/executionRunsColumns';
import { useExecutionRunsTableRowActions } from '@/features/executionRuns/components/useExecutionRunsTableRowActions';
import { useUpdateExecutionRun } from '@/features/executionRuns/hooks/useExecutionRunMutations';
import { useOrganisationFilter } from '@/features/organisations/providers/organisationHooks';
import { useProjectFilter } from '@/features/projects';
import { DetailEditEntityDialog } from '@/features/shared/components/entityDetails/DetailEditEntityDialog';
import {
  TABLE_GRID_HEADER_HEIGHT,
  TABLE_ROW_HEIGHT,
  TABLE_ROW_MEASURE_HEIGHT,
} from '@/features/shared/components/tableSystem/tableConstants';
import { tableSx } from '@/features/shared/components/tableSystem/tableSx';
import { useDetailEditEditor } from '@/features/shared/hooks/useDetailEditEditor';
import { useProjectStatusPresentation } from '@/features/shared/hooks/useProjectStatusPresentation';
import { buildTagColorByName } from '@/features/shared/tags';
import { executionRunsGridConfig } from '@/features/uploads/grid/gridConfig';
import { suiteRunStatusOptions } from '@/shared/constants/statusOptions';
import { getDialogMessage } from '@/shared/copy/dialogs';
import { gridPaths, useTagOptions } from '@/shared/hooks';
import { buildTableGridProps } from '@/ui/composites/grid/tableGridProps';
import { ServerDataTable } from '@/ui/composites/ServerDataTable';
import type { TagOption } from '@/shared/data/tags';
import type { SpacingToken } from '@/theme/tokens';
import type { GridQueryInput } from '@/ui/composites/grid/gridUtils';
import type {
  DataGridProps,
  GridColumnVisibilityModel,
  GridSortModel,
  SxProps,
  Theme,
} from '@/ui/primitives';
import type { ExecutionRunDto, QueryResult } from '@flowqa/shared';

type ExecutionRunsTableProps = {
  persistKey?: string | null;
  syncStateToUrl?: boolean;
  pageSizeOptions?: number[];
  initialPageSize?: number;
  disablePageSizeSelect?: boolean;
  search?: string;
  noRowsText?: string;
  fixedFilters?: GridQueryInput['filters'];
  queryFn?: (input: GridQueryInput) => Promise<QueryResult<ExecutionRunDto>>;
  queryKey?: QueryKey;
  initialData?: QueryResult<ExecutionRunDto>;
  refetchOnMount?: boolean | 'always';
  showLastUpdated?: boolean;
  onRowsChange?: (rows: ExecutionRunDto[]) => void;
  onQueryMeta?: (meta: { total: number; items: ExecutionRunDto[] } | null) => void;
  filteredNoRowsText?: string;
  skeletonCellPaddingY?: SpacingToken;
  sx?: SxProps<Theme>;
  containerSx?: SxProps<Theme>;
  initialSortModel?: GridSortModel;
  columnVisibilityModel?: GridColumnVisibilityModel;
  onColumnVisibilityModelChange?: DataGridProps['onColumnVisibilityModelChange'];
  disableColumnSorting?: boolean;
  disableColumnMenu?: boolean;
  tagOptionsOverride?: TagOption[];
  preservePreviousData?: boolean;
  showSkeletonOnFetch?: boolean;
  showPaginationSkeletonWhenRowsVisible?: boolean;
  dynamicRowHeight?: boolean;
  active?: boolean;
};

const stripExecutionRunSummaryDetails = (row: ExecutionRunDto): ExecutionRunDto => {
  if ((row.suiteRunSummaries?.length ?? 0) === 0 && (row.testRunSummaries?.length ?? 0) === 0) {
    return row;
  }

  return {
    ...row,
    suiteRunSummaries: [],
    testRunSummaries: [],
  };
};

const stripExecutionRunsSummaryDetails = (
  result: QueryResult<ExecutionRunDto>,
): QueryResult<ExecutionRunDto> => ({
  ...result,
  items: result.items.map(stripExecutionRunSummaryDetails),
});

export const ExecutionRunsTable = ({
  persistKey,
  syncStateToUrl = false,
  pageSizeOptions,
  initialPageSize,
  disablePageSizeSelect,
  search,
  noRowsText,
  fixedFilters,
  queryFn,
  queryKey,
  initialData,
  refetchOnMount,
  showLastUpdated = false,
  onRowsChange,
  onQueryMeta,
  filteredNoRowsText,
  skeletonCellPaddingY,
  sx,
  containerSx,
  initialSortModel,
  columnVisibilityModel,
  onColumnVisibilityModelChange,
  disableColumnSorting = true,
  disableColumnMenu = true,
  tagOptionsOverride,
  preservePreviousData = false,
  showSkeletonOnFetch = true,
  showPaginationSkeletonWhenRowsVisible = true,
  dynamicRowHeight = true,
  active = true,
}: ExecutionRunsTableProps): ReactElement => {
  const { session, loading } = useAuth();
  const { selectedOrganisationId } = useOrganisationFilter();
  const { selectedProjectId } = useProjectFilter();
  const [editTarget, setEditTarget] = useState<ExecutionRunDto | null>(null);
  const { tagOptions: fetchedTagOptions } = useTagOptions({
    projectId: selectedProjectId,
    enabled: tagOptionsOverride === undefined,
  });
  const tagOptions = tagOptionsOverride ?? fetchedTagOptions;
  const updateExecutionRun = useUpdateExecutionRun();
  const {
    deleteLoading,
    deleteTarget,
    deleteRowPendingId,
    onDeleteClose,
    onDeleteConfirm,
    onDeleteRow,
  } = useExecutionRunsTableRowActions({
    selectedProjectId,
  });
  const editEditor = useDetailEditEditor<ExecutionRunDto['status']>({
    projectId: editTarget?.projectId ?? null,
    successMessage: 'Execution run updated',
    saveEntity: async ({ tags, status, timestamp, durationMs }) => {
      if (!editTarget) return;
      const startedAt = timestamp ? timestamp.toISOString() : null;
      const finishedAt =
        timestamp && durationMs !== null && durationMs !== undefined
          ? new Date(timestamp.getTime() + durationMs).toISOString()
          : null;
      await updateExecutionRun.mutateAsync({
        id: editTarget.id,
        tags,
        startedAt,
        finishedAt,
        durationMs,
        status,
      });
    },
  });
  const resolvedPageSizeOptions = pageSizeOptions ?? [5, 10, 25, 50];
  const { groupErroredWithFailed } = useProjectStatusPresentation();
  const gridConfig = executionRunsGridConfig({
    organisationId: selectedOrganisationId,
    persistKey: persistKey === undefined ? 'execution-runs-table-v5' : persistKey,
    syncStateToUrl,
    fixedFilters,
    initialSortModel,
  });
  const tableQueryFn = useCallback(
    async (input: GridQueryInput): Promise<QueryResult<ExecutionRunDto>> => {
      const result = await (queryFn ?? gridConfig.queryFn)(input);
      return stripExecutionRunsSummaryDetails(result);
    },
    [gridConfig, queryFn],
  );
  const lightweightInitialData = useMemo(
    () => (initialData ? stripExecutionRunsSummaryDetails(initialData) : undefined),
    [initialData],
  );
  const gridProps = buildTableGridProps<ExecutionRunDto>({
    gridConfig,
    queryFn: tableQueryFn,
    queryKey,
    pageSizeOptions: resolvedPageSizeOptions,
    initialPageSize,
    disablePageSizeSelect,
    noRowsText,
    showLastUpdated,
    onRowsChange,
  });
  const tagColorByName = useMemo(() => buildTagColorByName(tagOptions), [tagOptions]);
  const columns = useMemo(
    () =>
      buildExecutionRunsColumns({
        groupErroredWithFailed,
        organisationId: selectedOrganisationId,
        editRowPendingId: editEditor.isPending ? (editTarget?.id ?? null) : null,
        deleteRowPendingId,
        onEditRow: (row) => {
          setEditTarget(row);
        },
        onDeleteRow,
        tagColorByName,
      }),
    [
      deleteRowPendingId,
      editEditor.isPending,
      editTarget?.id,
      groupErroredWithFailed,
      selectedOrganisationId,
      onDeleteRow,
      tagColorByName,
    ],
  );
  const togglableColumnFields = useMemo(
    () => columns.map((column) => column.field).filter((field) => field !== 'name'),
    [columns],
  );
  const editTimestamp = editTarget?.startedAt ?? editTarget?.finishedAt ?? null;

  return (
    <>
      <ServerDataTable<ExecutionRunDto>
        queryPath={gridPaths.executionRuns}
        {...gridConfig}
        {...gridProps}
        initialData={lightweightInitialData}
        columns={columns}
        getRowId={(row) => row.id}
        isAuthenticated={session !== null}
        externalLoading={loading}
        organisationId={selectedOrganisationId}
        projectId={selectedProjectId}
        search={search}
        onQueryMeta={onQueryMeta}
        refetchOnMount={refetchOnMount}
        preservePreviousData={preservePreviousData}
        showSkeletonOnFetch={showSkeletonOnFetch}
        showPaginationSkeletonWhenRowsVisible={showPaginationSkeletonWhenRowsVisible}
        active={active}
        columnVisibilityModel={columnVisibilityModel}
        disableColumnMenu={disableColumnMenu}
        disableColumnSorting={disableColumnSorting}
        interactionMode="static"
        getRowHeight={dynamicRowHeight ? () => 'auto' : undefined}
        skeletonRowHeight={TABLE_ROW_HEIGHT}
        skeletonGetRowHeight={dynamicRowHeight ? null : undefined}
        filteredNoRowsText={filteredNoRowsText}
        skeletonCellPaddingY={skeletonCellPaddingY}
        sx={[
          ...(Array.isArray(tableSx) ? tableSx : [tableSx]),
          {
            '& .MuiDataGrid-cell[data-field="status"], & .MuiDataGrid-cell[data-field="resultsFileName"], & .MuiDataGrid-cell[data-field="name"], & .MuiDataGrid-cell[data-field="suiteRunPassed"], & .MuiDataGrid-cell[data-field="testRunPassed"], & .MuiDataGrid-cell[data-field="actions"]':
              {
                alignItems: 'flex-start',
              },
            '& .MuiDataGrid-columnHeaders': {
              minHeight: `${TABLE_GRID_HEADER_HEIGHT}px !important`,
            },
          },
          ...(sx ? (Array.isArray(sx) ? sx : [sx]) : []),
        ]}
        containerSx={containerSx}
        disablePageSizeSelect={disablePageSizeSelect}
        autoHeight
        gridProps={{
          getEstimatedRowHeight: dynamicRowHeight ? () => TABLE_ROW_MEASURE_HEIGHT : undefined,
          rowHeight: dynamicRowHeight ? undefined : TABLE_ROW_HEIGHT,
          onColumnVisibilityModelChange,
          slotProps: {
            toolbar: {
              showQuickFilter: true,
              csvOptions: { disableToolbarButton: true },
              printOptions: { disableToolbarButton: true },
            },
            columnsManagement: {
              disableShowHideToggle: true,
              disableResetButton: true,
              getTogglableColumns: () => togglableColumnFields,
            },
          },
        }}
      />
      <DetailEditEntityDialog
        open={editTarget !== null}
        entityLabel="Execution Run"
        entityName={editTarget?.id ?? ''}
        projectId={editTarget?.projectId ?? null}
        tags={editTarget?.tags ?? []}
        status={editTarget?.status}
        statusOptions={suiteRunStatusOptions}
        timestamp={editTimestamp}
        durationMs={editTarget?.durationMs ?? null}
        timestampLabel="Timestamp"
        durationLabel="Duration"
        durationInputMode="timeParts"
        loading={editEditor.isPending || updateExecutionRun.isPending}
        cascadeCopy="Tags will be applied to all associated suite runs and test runs."
        onClose={() => {
          if (editEditor.isPending) return;
          setEditTarget(null);
        }}
        onSave={async ({ tags, colors, status, timestamp, durationMs }) => {
          await editEditor.save({ tags, colors, status, timestamp, durationMs });
          setEditTarget(null);
        }}
      />
      <ConfirmDialog
        open={deleteTarget !== null}
        title={getDialogMessage('destructive.delete.title', { entity: 'executionRun' })}
        description="This execution run and its associated suite runs and test runs will be deleted."
        confirmLabel={getDialogMessage('destructive.delete.confirmLabel', {
          entity: 'executionRun',
        })}
        confirmColor="error"
        loading={deleteLoading}
        onClose={onDeleteClose}
        onConfirm={onDeleteConfirm}
      />
    </>
  );
};
